import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import Topbar from './Topbar';
import MobileNavDrawer from './MobileNavDrawer';
import { useAuthShell } from './useAuthShell';
import LogoLoader from '../ui/LogoLoader';
import { SECTION_COLORS, sectionForPath } from '../../theme/sectionColors';

// Minimum time the full-page logo loader stays up after a page change.
const PAGE_LOADER_MS = 700;

const AppShell = ({ children }: { children: React.ReactNode }) => {
  const { user, active, avatarInitial, go, handleLogout } = useAuthShell();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Coarse route family (e.g. "notes" for /notes, /notes/create, /notes/edit/:slug) so
  // sub-route navigation (opening a dialog, changing a folder) doesn't remount the page
  // and lose its already-loaded state — only switching between top-level sections animates.
  const { pathname } = useLocation();
  const [pageLoading, setPageLoading] = useState(true);

  // Publish the current section's colours as CSS variables on <html> so buttons, tabs, nav pills and
  // portalled dialogs all pick up the same palette as the Flutter app's section tiles.
  useEffect(() => {
    const { a, b } = SECTION_COLORS[sectionForPath(pathname)];
    const root = document.documentElement;
    root.style.setProperty('--qs-a', a);
    root.style.setProperty('--qs-b', b);
  }, [pathname]);

  const routeFamily = active.split('/')[1] || 'home';

  // Only flash the page loader when moving between top-level sections. Opening or closing a popup
  // (/notes -> /notes/create -> /notes) stays in the same family, so it must not look like a reload.
  useEffect(() => {
    setPageLoading(true);
    const t = window.setTimeout(() => setPageLoading(false), PAGE_LOADER_MS);
    return () => window.clearTimeout(t);
  }, [routeFamily]);

  // Inside PMA the module draws its own header (PMA menu + back button), so the main-site chrome is hidden.
  const inPma = pathname.startsWith('/pma');

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {!inPma && (
      <Topbar
        user={user}
        avatarInitial={avatarInitial}
        active={active}
        go={go}
        onLogout={handleLogout}
        onOpenMobileNav={() => setMobileNavOpen(true)}
      />
      )}
      {!inPma && (
      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        active={active}
        go={go}
        onLogout={handleLogout}
      />
      )}

      <Container
        key={routeFamily}
        maxWidth="xl"
        className="qs-animate-in"
        sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, sm: 3 }, flex: 1, width: '100%', position: 'relative' }}
      >
        {children}
        {pageLoading && !inPma && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
            }}
          >
            <LogoLoader />
          </Box>
        )}
      </Container>

      <Box
        component="footer"
        sx={{
          py: { xs: 2, md: 2.5 },
          bgcolor: (theme) =>
            theme.palette.mode === 'light' ? 'rgba(255,255,255,0.7)' : 'rgba(23,25,35,0.7)',
          backdropFilter: 'blur(10px)',
          borderTop: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(98,38,167,0.10)' : 'rgba(255,255,255,0.08)'}`,
        }}
      >
        <Typography variant="body2" color="text.secondary" align="center" sx={{ px: 2 }}>
          © {new Date().getFullYear()} QuillStack. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default AppShell;
